import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Phone, MapPin, Clock } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal } from "./Reveal";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CITIES, SERVICES, COMPANY } from "@/data/content";

const inputCls =
  "w-full rounded-lg border border-brand-line bg-white px-4 py-3 text-brand-navy placeholder:text-slate-400 focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/10 focus:outline-none transition-colors duration-200";

export default function ContactForm({ defaultCity = "" }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: defaultCity,
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.name.length < 2)
      return toast.error("Veuillez indiquer votre nom.");
    if (!form.phone || form.phone.length < 6)
      return toast.error("Veuillez indiquer un téléphone valide.");
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await api.post("/leads", payload);
      toast.success("Demande envoyée ! Nous vous rappelons rapidement.");
      setForm({ name: "", phone: "", email: "", city: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-[1fr_1.1fr] gap-14">
        <Reveal>
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-brand-golddark">
            <span className="w-8 h-px bg-brand-gold" /> Contact
          </span>
          <h2 className="mt-5 font-display font-semibold text-4xl md:text-6xl tracking-tight text-brand-navy leading-[1.05]">
            Demandez votre devis gratuit
          </h2>
          <p className="mt-5 text-lg text-slate-600 max-w-md leading-relaxed">
            Une urgence ? Appelez directement. Sinon, laissez vos coordonnées :
            nous vous recontactons dans les plus brefs délais.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={`tel:${COMPANY.phoneTel}`}
              data-testid="contact-phone"
              className="flex items-center gap-4 group"
            >
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-brand-gold text-brand-navy">
                <Phone className="w-5 h-5" />
              </span>
              <span>
                <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium">
                  Urgence 24h/24
                </span>
                <span className="font-display font-semibold text-xl text-brand-navy group-hover:text-brand-golddark transition-colors duration-300">
                  {COMPANY.phoneDisplay}
                </span>
              </span>
            </a>
            <div className="flex items-center gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-brand-navy/5 text-brand-navy">
                <MapPin className="w-5 h-5" />
              </span>
              <span className="text-slate-700">{COMPANY.address}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-brand-navy/5 text-brand-navy">
                <Clock className="w-5 h-5" />
              </span>
              <span className="text-slate-700">Ouvert 24h/24 · 7j/7</span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-2xl border border-brand-line bg-brand-mist p-8 md:p-10 space-y-6 shadow-sm"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium mb-2 block">
                  Nom *
                </label>
                <input
                  data-testid="input-name"
                  className={inputCls}
                  placeholder="Votre nom"
                  value={form.name}
                  onChange={set("name")}
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium mb-2 block">
                  Téléphone *
                </label>
                <input
                  data-testid="input-phone"
                  className={inputCls}
                  placeholder="06 12 34 56 78"
                  value={form.phone}
                  onChange={set("phone")}
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium mb-2 block">
                Email
              </label>
              <input
                data-testid="input-email"
                type="email"
                className={inputCls}
                placeholder="vous@email.fr"
                value={form.email}
                onChange={set("email")}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium mb-2 block">
                  Ville
                </label>
                <Select
                  value={form.city}
                  onValueChange={(v) => setForm((f) => ({ ...f, city: v }))}
                >
                  <SelectTrigger
                    data-testid="select-city"
                    className="bg-white border-brand-line rounded-lg text-brand-navy"
                  >
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-brand-line text-brand-navy">
                    {CITIES.map((c) => (
                      <SelectItem key={c.slug} value={c.name}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium mb-2 block">
                  Service
                </label>
                <Select
                  value={form.service}
                  onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
                >
                  <SelectTrigger
                    data-testid="select-service"
                    className="bg-white border-brand-line rounded-lg text-brand-navy"
                  >
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-brand-line text-brand-navy">
                    {SERVICES.map((s) => (
                      <SelectItem key={s.slug} value={s.title}>
                        {s.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium mb-2 block">
                Message
              </label>
              <textarea
                data-testid="input-message"
                rows={3}
                className={inputCls + " resize-none"}
                placeholder="Décrivez votre besoin…"
                value={form.message}
                onChange={set("message")}
              />
            </div>

            <button
              type="submit"
              data-testid="contact-form-submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 bg-brand-gold hover:bg-brand-golddark disabled:opacity-60 text-brand-navy px-8 py-4 rounded-full font-semibold text-base transition-colors duration-300 active:scale-[0.98]"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {loading ? "Envoi…" : "Envoyer ma demande"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
